import { inject, isDevMode, makeStateKey, PLATFORM_ID, TransferState } from "@angular/core";
import { isPlatformBrowser, isPlatformServer, PlatformLocation } from "@angular/common";

import { MainPageComponentData } from "../models/main-page-component.model";
import { MainComponentService } from "../../services/main-component.service";

const MAIN_PAGE_COMPONENTS_KEY = makeStateKey<MainPageComponentData[]>('mainPageComponents');

const MAX_RETRIES = 2;
const TIMEOUT_MS = 5000;

export function initMainPageData() {

    const mainComponentService = inject(MainComponentService);
    const transferState = inject(TransferState);
    const platformId = inject(PLATFORM_ID);
    const platformLocation = inject(PlatformLocation);

    return async () => {
        const isBrowser = isPlatformBrowser(platformId);
        const isServer = isPlatformServer(platformId);

        // Static pages don't need main page components — skip Firestore fetch during pre-rendering
        if (isServer && platformLocation.pathname !== '/') {
            return;
        }

        // eslint-disable-next-line no-console
        if (isDevMode()) console.log(`Initializing main page data on APP_INITIALIZER (${isBrowser ? 'BROWSER' : 'SERVER'})...`);

        // If in browser and data exists in TransferState, use that instead of fetching again
        if (isBrowser && transferState.hasKey(MAIN_PAGE_COMPONENTS_KEY)) {
            // eslint-disable-next-line no-console
            if (isDevMode()) console.log('APP_INITIALIZER: Found data in TransferState, skipping fetch');
            const components = transferState.get<MainPageComponentData[]>(MAIN_PAGE_COMPONENTS_KEY, []);

            mainComponentService.setMainPageComponents(components);

            // Remove from transfer state so subsequent navigations work normally
            transferState.remove(MAIN_PAGE_COMPONENTS_KEY);

            return;
        }

        // Fetch data from Firestore with retry and timeout
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            let timeoutId: ReturnType<typeof setTimeout>;
            try {
                const components = await Promise.race([
                    mainComponentService.fetchMainComponents(),
                    new Promise<never>((_, reject) => {
                        timeoutId = setTimeout(() => reject(new Error('Timeout')), TIMEOUT_MS);
                    })
                ]);
                clearTimeout(timeoutId!);

                // If on server and we have data, store it in TransferState
                if (isServer && components && components.length > 0) {
                    // eslint-disable-next-line no-console
                    if (isDevMode()) console.log('APP_INITIALIZER: Storing data in TransferState');
                    transferState.set(MAIN_PAGE_COMPONENTS_KEY, components);
                }
                return;
            } catch (err) {
                clearTimeout(timeoutId!);
                if (attempt === MAX_RETRIES) {
                    console.error('Error fetching main page components:', err);
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
    };
}
